/**
 * @fileoverview One prospect row of the Sales Pipeline backlog table.
 * @description Renders a backend prospect (GET /sales/prospects): bank and tenant code, type,
 * location, tier, branches, owner, stage and its open target (name-less summary: period and
 * overdue-or-outstanding status). The whole row opens the prospect's detail view; the bank name
 * stays a real button for keyboard users. `selection` adds the checkbox used to pick backlog rows for
 * Assign Target. The actions menu offers View plus whichever of Assign/Change owner, Edit, Archive
 * and Restore the page hands a handler for (each backed by the capability and state rules the page
 * applies). `legacy` is only passed while the mock API is on: it carries the matching mock Tenant so
 * the menu keeps the actions the backend does not serve yet (follow-up, activity, reassign, payment).
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import Badge from '@/shared/components/atoms/Badge'
import Checkbox from '@/shared/components/atoms/Checkbox'
import RowActionsMenu from '@/shared/components/molecules/RowActionsMenu'

import { PIPELINE_GRID_COLUMNS } from './PipelineRow'
import TenantRowActions from './TenantRowActions'

import type { TenantAction } from '@/features/sales/lib/tenant-access.utils'
import type { TargetWithOverdue } from '@/features/sales/mocks/targets-mock-store'
import type { ProspectListItem, ProspectTargetSummary } from '@/features/sales/types/prospect.types'
import type { SalesUserRecord, Tenant } from '@/features/sales/types/tenant.types'
import type { RowAction } from '@/shared/components/molecules/RowActionsMenu'

/**
 * @class
 * @description The mock-only Tenant/Target model behind a row, while the mock API is on.
 */
export interface ProspectRowLegacy {
  tenant: Tenant
  target: TargetWithOverdue | undefined
  roster: SalesUserRecord[]
  viewerUserId: string
  viewerRole: string
  onAction: (action: TenantAction) => void
}

/** @description The row's Assign Target checkbox. */
export interface ProspectRowSelection {
  selected: boolean
  onToggle: () => void
}

/** @description The mock-only actions the real backend's edit, archive and restore replace. */
const SERVED_BY_BACKEND: TenantAction[] = [
  'edit',
  'editTarget',
  'updateStage',
  'restore',
  'archive',
]

interface ProspectRowProps {
  prospect: ProspectListItem
  onView: () => void
  selection?: ProspectRowSelection | undefined
  legacy?: ProspectRowLegacy | undefined
  onAssignOwner?: (() => void) | undefined
  onEdit?: (() => void) | undefined
  onArchive?: (() => void) | undefined
  onRestore?: (() => void) | undefined
}

/**
 * @description Today's date as YYYY-MM-DD, for judging whether a target's due date has passed.
 * @returns {string} Today in the browser's time zone.
 */
function todayIso(): string {
  return new Date().toLocaleDateString('en-CA')
}

/**
 * @description Renders the Target Status cell: overdue or outstanding, or "No target" when none.
 * @param {{ target: ProspectTargetSummary | null }} props - The row's open target, if any.
 * @param {ProspectTargetSummary | null} props.target - The target.
 */
function TargetStatusCell({ target }: { target: ProspectTargetSummary | null }) {
  const { t } = useTranslation()
  if (!target) {
    return (
      <span className="text-[12.5px] text-footer" data-testid="prospect-target-status">
        {t('sales.pipeline.noTarget')}
      </span>
    )
  }
  const overdue = target.dueDate < todayIso()
  return (
    <Badge variant={overdue ? 'danger' : 'neutral'} data-testid="prospect-target-status">
      {t(`sales.tenantDetail.target.statusValues.${overdue ? 'overdue' : 'outstanding'}`)}
    </Badge>
  )
}

/**
 * @component
 * @param {ProspectRowProps} props - The prospect, its handlers and any mock-only model.
 * @param {ProspectListItem} props.prospect - The row's prospect.
 * @param {() => void} props.onView - Opens the prospect's detail view.
 * @param {ProspectRowSelection} [props.selection] - The Assign Target checkbox, when selectable.
 * @param {ProspectRowLegacy} [props.legacy] - The mock-only model, while the mock API is on.
 * @param {() => void} [props.onAssignOwner] - Opens Assign Owner, when the viewer may assign.
 * @param {() => void} [props.onEdit] - Opens Edit Prospect, when the viewer may edit.
 * @param {() => void} [props.onArchive] - Opens Archive, when the viewer may archive.
 * @param {() => void} [props.onRestore] - Opens Restore, when the viewer may restore.
 */
function ProspectRow({
  prospect,
  onView,
  selection,
  legacy,
  onAssignOwner,
  onEdit,
  onArchive,
  onRestore,
}: ProspectRowProps) {
  const { t } = useTranslation()
  const legacyTarget = legacy?.target
  const archived = prospect.archivedAt !== null

  const actions: RowAction[] = [
    ...(onAssignOwner
      ? [
          {
            key: 'assignOwner',
            label: t(
              prospect.currentOwner
                ? 'sales.pipeline.assignOwner.menuChange'
                : 'sales.pipeline.assignOwner.menuAssign',
            ),
            onSelect: onAssignOwner,
          },
        ]
      : []),
    ...(onEdit ? [{ key: 'edit', label: t('sales.pipeline.edit.menu'), onSelect: onEdit }] : []),
    ...(onRestore
      ? [{ key: 'restore', label: t('sales.pipeline.restore.menu'), onSelect: onRestore }]
      : []),
    ...(onArchive
      ? [
          {
            key: 'archive',
            label: t('sales.pipeline.archive.menu'),
            onSelect: onArchive,
            tone: 'danger' as const,
          },
        ]
      : []),
  ]

  return (
    <div
      onClick={onView}
      data-testid="prospect-row"
      className={`grid ${PIPELINE_GRID_COLUMNS} cursor-pointer items-center gap-3 border-b border-row-border px-4 py-2.5 hover:bg-surface-hover sm:px-5`}
    >
      <span onClick={(event) => event.stopPropagation()} className="flex">
        {selection && (
          <Checkbox
            checked={selection.selected}
            onChange={selection.onToggle}
            aria-label={prospect.bankName}
            data-testid="prospect-select-checkbox"
          />
        )}
      </span>
      <div className="flex min-w-0 flex-col">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            onView()
          }}
          data-testid="prospect-name-button"
          className="min-w-0 cursor-pointer truncate text-left text-[13.5px] font-semibold text-primary hover:underline"
        >
          {prospect.bankName}
        </button>
        <span className="flex items-center gap-1.5 truncate text-xs text-footer">
          <span data-testid="prospect-tenant-code">{prospect.tenantCode}</span>
          {archived && (
            <span title={prospect.archiveReason ?? undefined}>
              <Badge variant="neutral" data-testid="prospect-archived-badge">
                {t('sales.pipeline.archivedBadge')}
              </Badge>
            </span>
          )}
        </span>
      </div>
      <span className="truncate text-[13px] text-cell" data-testid="prospect-bank-type">
        {prospect.bankType.name}
      </span>
      <span className="truncate text-[13px] text-cell" data-testid="prospect-location">
        {prospect.location.city}, {prospect.location.state}
      </span>
      <span className="text-[13px] font-medium text-cell" data-testid="prospect-tier">
        {prospect.tier.name}
      </span>
      <span className="text-[13px] text-cell" data-testid="prospect-branches">
        {prospect.branchCount}
      </span>
      <span
        data-testid="prospect-owner"
        className={`truncate text-[13px] ${prospect.currentOwner ? 'text-cell' : 'font-semibold text-danger'}`}
      >
        {prospect.currentOwner?.displayName ?? t('sales.pipeline.filters.unassigned')}
      </span>
      <span className="truncate text-[13px] text-cell" data-testid="prospect-stage">
        {prospect.salesStage ? t(`sales.pipeline.stages.${prospect.salesStage}`) : '—'}
      </span>
      <span className="truncate text-[13px] text-cell" data-testid="prospect-target-period">
        {prospect.target?.periodLabel ?? '—'}
      </span>
      <TargetStatusCell target={prospect.target} />
      <span className="text-[13px] text-cell" data-testid="prospect-next-follow-up">
        {legacyTarget?.nextFollowUpAt ? legacyTarget.nextFollowUpAt.slice(0, 10) : '—'}
      </span>
      {legacy ? (
        <TenantRowActions
          tenant={legacy.tenant}
          target={legacy.target}
          roster={legacy.roster}
          viewerUserId={legacy.viewerUserId}
          viewerRole={legacy.viewerRole}
          onView={onView}
          onAction={legacy.onAction}
          omit={SERVED_BY_BACKEND}
          extraActions={actions}
        />
      ) : (
        <RowActionsMenu
          label={t('sales.pipeline.rowActions', { name: prospect.bankName })}
          actions={[
            { key: 'view', label: t('sales.activeClients.view'), onSelect: onView },
            ...actions,
          ]}
        />
      )}
    </div>
  )
}

export default ProspectRow
